import React from 'react';
import { Button } from '../../ui/button';
import { Mail, Clock, ChevronDown, ChevronRight, Reply, CheckCheck, Archive, MailOpen } from 'lucide-react';

const STATUS_STYLES = {
  new: { bg: 'bg-[#7dd3fc]/20', text: 'text-[#7dd3fc]', label: 'New' },
  read: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'Read' },
  replied: { bg: 'bg-green-500/20', text: 'text-green-500', label: 'Replied' },
  archived: { bg: 'bg-[#1e3a5f]/40', text: 'text-[#888680]', label: 'Archived' },
};

export const SubmissionCard = ({ submission, isExpanded, onToggle, onStatusChange }) => {
  const status = STATUS_STYLES[submission.status] || STATUS_STYLES.new;
  const date = new Date(submission.created_at);

  const replyHref = `mailto:${submission.email}?subject=${encodeURIComponent('Re: ' + submission.subject)}`;

  return (
    <div
      data-testid={`submission-${submission.submission_id}`}
      className={`bg-[#302f2c] border rounded-xl overflow-hidden transition-all ${
        submission.status === 'new' ? 'border-[#7dd3fc]/40' : 'border-[#1e3a5f]/50'
      }`}
    >
      <button
        onClick={onToggle}
        data-testid={`submission-toggle-${submission.submission_id}`}
        className="w-full text-left p-4 flex items-start gap-3 hover:bg-[#1e3a5f]/10 transition-colors"
      >
        <div className="mt-1 text-[#888680]">
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[#7dd3fc] font-semibold truncate">{submission.name}</span>
            <span className={`${status.bg} ${status.text} px-2 py-0.5 rounded-full text-xs font-medium`}>
              {status.label}
            </span>
          </div>
          <p className="text-[#888680] text-sm truncate mt-0.5">{submission.subject}</p>
          {!isExpanded && (
            <p className="text-[#888680]/70 text-xs truncate mt-1">{submission.message}</p>
          )}
        </div>
        <div className="flex items-center gap-1 text-[#888680] text-xs whitespace-nowrap">
          <Clock size={12} />
          {date.toLocaleDateString()}
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 pt-1 border-t border-[#1e3a5f]/30">
          <div className="flex items-center gap-2 text-[#888680] text-sm mb-3 mt-3">
            <Mail size={14} />
            <a href={`mailto:${submission.email}`} className="text-[#7dd3fc] hover:underline">
              {submission.email}
            </a>
            <span className="text-[#888680]/60">·</span>
            <span>{date.toLocaleString()}</span>
          </div>

          <div className="bg-[#1a1c1b] rounded-lg p-4 text-[#d6d3d1] text-sm leading-relaxed whitespace-pre-wrap mb-4">
            {submission.message}
          </div>

          <div className="flex flex-wrap gap-2">
            <a href={replyHref}>
              <Button
                data-testid={`reply-${submission.submission_id}`}
                className="bg-[#7dd3fc] text-[#1a1c1b] hover:bg-[#7dd3fc]/90 rounded-full px-4 py-2 text-xs font-semibold flex items-center gap-1.5"
              >
                <Reply size={14} />
                Reply
              </Button>
            </a>
            {submission.status !== 'read' && (
              <Button
                onClick={() => onStatusChange(submission.submission_id, 'read')}
                variant="outline"
                data-testid={`mark-read-${submission.submission_id}`}
                className="border-[#1e3a5f] text-[#888680] hover:text-[#7dd3fc] hover:border-[#7dd3fc]/50 rounded-full px-4 py-2 text-xs flex items-center gap-1.5"
              >
                <MailOpen size={14} />
                Mark Read
              </Button>
            )}
            {submission.status !== 'replied' && (
              <Button
                onClick={() => onStatusChange(submission.submission_id, 'replied')}
                variant="outline"
                data-testid={`mark-replied-${submission.submission_id}`}
                className="border-[#1e3a5f] text-[#888680] hover:text-green-500 hover:border-green-500/50 rounded-full px-4 py-2 text-xs flex items-center gap-1.5"
              >
                <CheckCheck size={14} />
                Mark Replied
              </Button>
            )}
            {submission.status !== 'archived' && (
              <Button
                onClick={() => onStatusChange(submission.submission_id, 'archived')}
                variant="outline"
                data-testid={`archive-${submission.submission_id}`}
                className="border-[#1e3a5f] text-[#888680] hover:text-[#7dd3fc] hover:border-[#7dd3fc]/50 rounded-full px-4 py-2 text-xs flex items-center gap-1.5"
              >
                <Archive size={14} />
                Archive
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
